/**
 * Fire-and-forget error reporting from the data layer. Hooks and one-shot
 * fetches call `reportDataError` on failure instead of only console.logging, so
 * a single UI subscriber (ToastProvider) can surface load failures to the user
 * without every screen having to destructure and check `.error`.
 */
export interface DataErrorPayload {
  /** Human label of the failing source — typically the collection name. */
  source: string;
  raw: Error;
}

type DataErrorListener = (payload: DataErrorPayload) => void;

const listeners = new Set<DataErrorListener>();

/** Subscribe to data errors. Returns an unsubscribe function. */
export function onDataError(listener: DataErrorListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Report a data-layer failure. Never throws, even if a listener misbehaves. */
export function reportDataError(source: string, raw: Error): void {
  const payload: DataErrorPayload = { source, raw };
  for (const listener of listeners) {
    try {
      listener(payload);
    } catch {
      // A broken listener must never break reporting.
    }
  }
}
